import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Building2, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLogout } from "@/hooks/use-auth";
import {
  type LocationSettings,
  useActiveLocation,
  useLocationSettings,
  useLocations,
  useUpdateLocationSettings,
} from "@/hooks/use-locations";
import { useAuthStore } from "@/store/auth-store";
import LocationSchedulePanel from "./LocationSchedulePanel";
import { KitchenCapacityPanel } from "./KitchenCapacityPanel";
import { SpecialEventsPanel } from "./SpecialEventsPanel";
import { ZonesBookabilityPanel } from "./ZonesBookabilityPanel";
import { TableMaintenancePanel } from "./TableMaintenancePanel";

type FormState = {
  default_reservation_duration_minutes: number | "";
  no_show_tolerance_minutes: number | "";
  auto_complete_grace_minutes: number | "";
  proximity_window_minutes: number | "";
  turnover_buffer_minutes: number | "";
  minimum_advance_minutes: number | "";
};

const FIELD_LABELS: Record<keyof FormState, string> = {
  default_reservation_duration_minutes: "Límite por mesa (minutos)",
  no_show_tolerance_minutes: "Tolerancia no-show (minutos)",
  auto_complete_grace_minutes: "Gracia para completar (minutos)",
  proximity_window_minutes: "Proximidad reserva activa (minutos)",
  turnover_buffer_minutes: "Margen entre reservas (minutos)",
  minimum_advance_minutes: "Antelación mínima para reservar (minutos)",
};

const FIELD_HINTS: Record<keyof FormState, string> = {
  default_reservation_duration_minutes:
    "Tiempo estimado que una reserva ocupa la mesa. Lo usamos para calcular cuándo vuelve a estar disponible. Recomendado: 180 min (3 h).",
  no_show_tolerance_minutes:
    "Minutos de espera después de la hora reservada antes de marcar al cliente como no-show. Pasado este tiempo, la mesa queda libre para walk-ins.",
  auto_complete_grace_minutes:
    "Minutos después de la hora de fin antes de cerrar la reserva automáticamente. Si extendés ends_at manualmente, se respeta el nuevo valor.",
  proximity_window_minutes:
    "Cuántos minutos antes de la hora reservada la reserva aparece como Activa en el panel, para que el equipo se prepare a recibir al cliente.",
  turnover_buffer_minutes:
    "Margen de rotación que se suma a la ventana de cada reserva al chequear disponibilidad de mesas.",
  minimum_advance_minutes:
    "Con cuánta anticipación puede reservar un comensal desde la web. Ej: 180 = tres horas; los horarios más cercanos no se le muestran, y si ya no queda ninguno, el día tampoco. No aplica al panel, a los walk-ins ni al agente: si alguien llama diciendo que llega en 20 minutos, se le puede tomar igual. 0 desactiva la regla.",
};

const FIELD_BOUNDS: Record<keyof FormState, { min: number; max: number }> = {
  default_reservation_duration_minutes: { min: 30, max: 600 },
  no_show_tolerance_minutes: { min: 5, max: 60 },
  auto_complete_grace_minutes: { min: 0, max: 240 },
  proximity_window_minutes: { min: 15, max: 180 },
  turnover_buffer_minutes: { min: 0, max: 60 },
  minimum_advance_minutes: { min: 0, max: 1440 },
};

const FIELD_KEYS = Object.keys(FIELD_LABELS) as (keyof FormState)[];

function pickFormState(settings: LocationSettings): FormState {
  return {
    default_reservation_duration_minutes: settings.default_reservation_duration_minutes,
    no_show_tolerance_minutes: settings.no_show_tolerance_minutes,
    auto_complete_grace_minutes: settings.auto_complete_grace_minutes,
    proximity_window_minutes: settings.proximity_window_minutes,
    turnover_buffer_minutes: settings.turnover_buffer_minutes,
    minimum_advance_minutes: settings.minimum_advance_minutes,
  };
}

export default function SettingsPage() {
  return (
    <div className="flex-1 overflow-y-auto px-6 py-10 sm:py-14 md:pl-24">
      <header className="mb-10">
        <h1 className="text-3xl font-bold tracking-tight">Configuración</h1>
      </header>

      <Tabs defaultValue="general" className="flex flex-col gap-6">
        <TabsList variant="default" className="self-start !h-12 p-1">
          <TabsTrigger value="general" className="!text-base px-5">
            General
          </TabsTrigger>
          <TabsTrigger value="schedule" className="!text-base px-5">
            Horarios
          </TabsTrigger>
          <TabsTrigger value="kitchen-capacity" className="!text-base px-5">
            Cocina
          </TabsTrigger>
          <TabsTrigger value="events" className="!text-base px-5">
            Eventos
          </TabsTrigger>
          <TabsTrigger value="zones" className="!text-base px-5">
            Zonas
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="!text-base px-5">
            Mantenimiento
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <GeneralSettingsForm />
        </TabsContent>
        <TabsContent value="schedule">
          <LocationSchedulePanel />
        </TabsContent>
        <TabsContent value="kitchen-capacity">
          <KitchenCapacityPanel />
        </TabsContent>
        <TabsContent value="events">
          <SpecialEventsPanel />
        </TabsContent>
        <TabsContent value="zones">
          <ZonesBookabilityPanel />
        </TabsContent>
        <TabsContent value="maintenance">
          <TableMaintenancePanel />
        </TabsContent>
      </Tabs>

      {/* Cuenta — mobile-only block. On desktop these actions live in the
          left rail (Navbar.tsx), so we only surface them here for the
          small viewport where the rail is replaced by the bottom nav. */}
      <AccountActionsMobile />
    </div>
  );
}

function AccountActionsMobile() {
  const navigate = useNavigate();
  const logout = useLogout();
  const setLocation = useAuthStore((s) => s.setLocation);
  const { data: locations } = useLocations();
  const hasMultipleLocations = (locations?.length ?? 0) > 1;

  const handleLogout = () => {
    logout.mutate(undefined, { onSuccess: () => navigate({ to: "/login" }) });
  };

  const handleChangeLocation = () => {
    setLocation(null);
    navigate({ to: "/select-location" });
  };

  return (
    <section className="md:hidden mt-10 flex flex-col gap-3">
      <h2 className="text-lg font-semibold tracking-tight">Cuenta</h2>
      <div className="flex flex-col gap-2">
        {hasMultipleLocations && (
          <Button
            variant="outline"
            onClick={handleChangeLocation}
            className="h-12 justify-start gap-3 px-4 text-base"
          >
            <Building2 className="w-5 h-5 shrink-0" />
            Cambiar sede
          </Button>
        )}
        <Button
          variant="outline"
          onClick={handleLogout}
          disabled={logout.isPending}
          className="h-12 justify-start gap-3 px-4 text-base text-destructive hover:text-destructive"
        >
          <LogOut className="w-5 h-5 shrink-0" />
          Cerrar sesión
        </Button>
      </div>
    </section>
  );
}

// ``<input type="time">`` always emits "HH:MM"; the API stores "HH:MM:SS".
// Normalize both directions so dirty-checks compare apples to apples.
function trimSeconds(value: string): string {
  return value.length > 5 ? value.slice(0, 5) : value;
}

function GeneralSettingsForm() {
  const activeLocation = useActiveLocation();
  const { data: settings, isLoading } = useLocationSettings();
  const updateMutation = useUpdateLocationSettings();
  const [form, setForm] = useState<FormState | null>(null);
  const [splitTime, setSplitTime] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset to server state when it loads or when the active location changes.
  useEffect(() => {
    if (settings) {
      setForm(pickFormState(settings));
      setSplitTime(trimSeconds(settings.night_shift_split_time));
    }
  }, [settings]);

  const changes = useMemo(() => {
    if (!form || !settings) return [];
    const numericChanges = FIELD_KEYS.filter((k) => form[k] !== settings[k]).map((k) => ({
      key: k as keyof LocationSettings,
      label: FIELD_LABELS[k],
      from: String(settings[k]),
      to: String(form[k]),
    }));
    const serverSplit = trimSeconds(settings.night_shift_split_time);
    if (splitTime !== serverSplit) {
      numericChanges.push({
        key: "night_shift_split_time",
        label: "Hora de corte día/noche",
        from: serverSplit,
        to: splitTime,
      });
    }
    return numericChanges;
  }, [form, settings, splitTime]);

  const isDirty = changes.length > 0;
  const hasEmpty = form === null || splitTime === "" || FIELD_KEYS.some((k) => form[k] === "");
  const canSave = isDirty && !hasEmpty && !updateMutation.isPending;

  if (isLoading || !form || !settings) {
    return (
      <div className="w-full max-w-2xl text-sm text-muted-foreground">Cargando configuración…</div>
    );
  }

  const handleNumberChange = (key: keyof FormState) => (raw: string) => {
    setForm((current) =>
      current ? { ...current, [key]: raw === "" ? "" : Number(raw) } : current,
    );
  };

  const handleReset = () => {
    setForm(pickFormState(settings));
    setSplitTime(trimSeconds(settings.night_shift_split_time));
  };

  const handleSave = () => {
    if (!canSave) return;
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!canSave || !form) return;
    const payload: Partial<LocationSettings> = {};
    for (const change of changes) {
      if (change.key === "night_shift_split_time") {
        // API expects "HH:MM:SS"; the input gives "HH:MM".
        payload.night_shift_split_time = `${splitTime}:00`;
      } else {
        const numericKey = change.key as Exclude<keyof FormState, never>;
        payload[numericKey] = Number(form[numericKey]);
      }
    }
    updateMutation.mutate(payload, {
      onSuccess: () => {
        setConfirmOpen(false);
      },
    });
  };

  return (
    <div className="w-full max-w-2xl">
      <form
        className="flex flex-col gap-6"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label className="text-base font-medium">Local</Label>
          <Input
            type="text"
            value={activeLocation?.name ?? ""}
            readOnly
            disabled
            className="h-12 text-base px-4 bg-muted"
          />
          <p className="text-xs leading-relaxed text-muted-foreground">
            Esta configuración aplica al local activo. Cambialo desde el selector de la barra
            superior.
          </p>
        </div>

        {FIELD_KEYS.map((key) => {
          const bounds = FIELD_BOUNDS[key];
          return (
            <div key={key} className="flex flex-col gap-2">
              <Label htmlFor={`config-${key}`} className="text-base font-medium">
                {FIELD_LABELS[key]}
              </Label>
              <div className="relative w-40">
                <Input
                  id={`config-${key}`}
                  type="number"
                  inputMode="numeric"
                  value={form[key]}
                  min={bounds.min}
                  max={bounds.max}
                  onChange={(e) => handleNumberChange(key)(e.target.value)}
                  className="h-12 pl-4 pr-14 text-base tabular-nums text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-sm font-medium text-muted-foreground">
                  min
                </span>
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">{FIELD_HINTS[key]}</p>
            </div>
          );
        })}

        <div className="flex flex-col gap-2">
          <Label htmlFor="config-night-shift-split-time" className="text-base font-medium">
            Hora de corte día/noche
          </Label>
          <div className="w-40">
            <Input
              id="config-night-shift-split-time"
              type="time"
              value={splitTime}
              onChange={(e) => setSplitTime(e.target.value)}
              className="h-12 px-4 text-base tabular-nums"
            />
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Hora a partir de la cual las reservas cuentan como turno Noche en el resumen de
            comensales del sidebar (antes cuentan como Día). Por defecto: 18:00.
          </p>
        </div>

        <div className="mt-4 flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={!isDirty || updateMutation.isPending}
            className="h-11 px-5"
          >
            Descartar
          </Button>
          <Button
            type="submit"
            disabled={!canSave}
            title={hasEmpty ? "Completá todos los campos numéricos" : undefined}
            className="h-11 px-6"
          >
            {updateMutation.isPending ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </form>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar cambios</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a actualizar la configuración del local. Revisá los cambios antes de continuar.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <ul className="mt-2 flex flex-col gap-2 text-sm">
            {changes.map((c) => (
              <li key={c.key} className="rounded-md border bg-muted/40 px-3 py-2">
                <div className="font-medium">{c.label}</div>
                <div className="text-muted-foreground">
                  <span className="line-through">{String(c.from)}</span>
                  <span className="mx-2">→</span>
                  <span className="text-foreground">{String(c.to)}</span>
                </div>
              </li>
            ))}
          </ul>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={updateMutation.isPending}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
